import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect, useMemo } from "react";
import Table from "../Table";
import QuickBooksLogo from "../../../assets/quickbooks-1.svg";
import InvoiceModal from "../InvoiceModal";
import PaymentRefundModal from "../PaymentRefundModal";
import { setCurrentInvoice } from "../../../store/slices/invoiceSlice";
import { setCurrentPayment } from "../../../store/slices/paymentsSlice";
import { refundPayment } from "../../../store/thunks/paymentsThunk";
import { fetchInvoicePaymentsByAppointmentId } from "../../../store/thunks/paymentsThunk";
import { toast } from 'react-toastify';

export const AppointmentPayments = () => {
  const dispatch = useDispatch();
  const rawPayments = useSelector((state) => state.payments.appointmentPayments);
  const invoices = useSelector((state) => state.invoices.appointmentInvoices);
  const isLoading = useSelector((state) => state.payments.loading);
  const clinic = useSelector((state) => state.clinics.selectedClinic);
  
  // Get invoice IDs for the current appointment to filter deposit usage
  const appointmentInvoiceIds = useMemo(() => 
    (invoices || []).map(invoice => invoice._id), 
    [invoices]
  );

  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedPaymentForRefund, setSelectedPaymentForRefund] = useState(null);
  const [selectedInvoiceForRefund, setSelectedInvoiceForRefund] = useState(null);
  const [key, setKey] = useState(0);
  
  // Get current user for permission checking
  const currentUser = useSelector((state) => state.auth.user);
  const hasRefundPermission = currentUser && currentUser.role && Array.isArray(currentUser.role) && (
    currentUser.role.includes('owner') || 
    currentUser.role.includes('admin')
  );

  // Normalize payments structure and split deposit payments by usage
  const normalizedPayments = useMemo(() => {
    const payments = [];
    
    if (rawPayments) {
      rawPayments.forEach((payment) => {
        let paymentObj;
      
      if (payment.paymentDate) {
        // Case: Loaded from appointment, create a consistent `payment` object
        paymentObj = {
          paymentDate: payment.paymentDate,
          paymentMethod: payment.paymentMethod,
          amount: payment.amount,
          invoiceId: payment.invoiceId,
          QBOPaymentId: payment.QBOPaymentId,
          deposit: payment.deposit,
          depositUsage: payment.depositUsage,
          convertedToDeposit: payment.convertedToDeposit, // Include conversion flag
          _id: payment._id,
        };
      } else {
        // Case: Already in `payment` object structure
        paymentObj = payment.payment;
      }

      // Note: Don't skip converted payments, but mark them to prevent confusion
      // We want to show them in the list with clear indication they were converted

      // For deposit payments with usage, create separate entries for each usage in this appointment
      if (paymentObj.deposit && paymentObj.depositUsage?.length > 0) {
        const appointmentUsages = paymentObj.depositUsage.filter(usage => 
          appointmentInvoiceIds.includes(usage.invoiceId)
        );
        
        appointmentUsages.forEach((usage, usageIndex) => {
          payments.push({
            payment: {
              ...paymentObj,
              _id: `${paymentObj._id}-usage-${usageIndex}`, // Create unique ID for each usage entry
              amount: usage.amountUsed, // Show the amount used for this specific invoice
              invoiceId: { invoiceId: usage.invoiceNumber }, // Show the specific invoice
              depositUsage: [usage], // Keep only this specific usage
              isDepositUsageEntry: true, // Flag to identify this as a split entry
            },
          });
        });
      } else {
        // For regular payments, add as-is
        payments.push({
          payment: paymentObj,
        });
      }
      });
    }
    
    return payments;
  }, [rawPayments, appointmentInvoiceIds]);



  const handleRowClick = (payment) => {
    const invoiceId = payment?.payment?.invoiceId?._id;

    if (!invoiceId) {
      console.error("Invoice ID is missing or invalid:", payment);
      return;
    }

    const invoice = invoices.find((inv) => inv._id === invoiceId);

    if (!invoice) {
      console.error("No matching invoice found for payment:", payment);
      return;
    }

    dispatch(setCurrentPayment(payment));
    dispatch(setCurrentInvoice(invoice));
    setShowInvoiceModal(true);
  };

  const getPaymentMethodDisplay = (payment) => {
    if (payment.payment.deposit) {
      return "Deposit";
    }
    
    const paymentMethod = clinic?.paymentMethods?.find(
      (method) => method.value === payment.payment.paymentMethod
    );
    return paymentMethod?.englishDescription || payment.payment.paymentMethod;
  };

  const getPaymentDetails = (payment) => {
    if (payment.payment.isDepositUsageEntry) {
      // Find the original deposit payment to get the deposit date
      const originalDeposit = rawPayments.find(p => 
        p._id === payment.payment._id || 
        (p.paymentDate && p._id === payment.payment._id)
      );
      
      // Use the original deposit's payment date, not the usage date
      const depositDate = originalDeposit ? 
        new Date(originalDeposit.paymentDate).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: "UTC"
        }) : "Unknown Date";
        
      return (
        <div className="flex flex-col">
          <span>Deposit Applied</span>
          <span className="text-xs text-gray-500">
            From Deposit ({depositDate})
          </span>
        </div>
      );
    }
    
    // Check if payment was converted to deposit
    if (payment.payment.convertedToDeposit) {
      return (
        <div className="flex flex-col">
          <span className="text-blue-600 font-medium">Converted to Deposit</span>
          <span className="text-xs text-gray-500">
            Originally: {getPaymentMethodDisplay(payment)}
          </span>
        </div>
      );
    }
    
    return getPaymentMethodDisplay(payment);
  };

  const handleRefundPayment = (payment) => {
    const invoiceId = payment?.payment?.invoiceId?._id;
    const invoice = invoices.find((inv) => inv._id === invoiceId);
    
    if (!invoice) {
      console.error("No matching invoice found for payment:", payment);
      toast.error("Unable to find invoice for this payment");
      return;
    }
    
    if (invoice.status !== 'refunded') {
      toast.error("The invoice must be refunded before you can refund individual payments");
      return;
    }
    
    setSelectedPaymentForRefund(payment.payment);
    setSelectedInvoiceForRefund(invoice);
    setShowRefundModal(true);
  };

  const handlePaymentRefundConfirm = async (refundData) => {
    try {
      console.log(`[Payment Refund] Processing payment refund:`, {
        payment: selectedPaymentForRefund,
        refundData
      });
      
      await dispatch(refundPayment({
        paymentId: selectedPaymentForRefund._id,
        refundType: refundData.refundType,
        paymentMethod: refundData.paymentMethod,
        reason: refundData.reason
      })).unwrap();
      
      if (refundData.refundType === 'refund') {
        toast.success("Payment refund processed successfully");
      } else {
        toast.success("Payment converted to deposit successfully");
      }
      
      // Refresh payment data for the current appointment
      const appointmentId = selectedInvoiceForRefund?.appointmentId;
      if (appointmentId) {
        await dispatch(fetchInvoicePaymentsByAppointmentId(appointmentId));
      }
      
    } catch (error) {
      console.error(`[Payment Refund] Error processing payment refund:`, error);
      
      // Handle specific error cases
      if (error.error && error.error.includes('must be refunded')) {
        toast.error("The invoice must be refunded before you can refund individual payments");
      } else if (error.error && error.error.includes('privileges')) {
        toast.error("You don't have permission to refund payments. Only owners and administrators can perform refunds.");
      } else {
        toast.error(error.error || "Failed to process payment refund");
      }
    }
  };



  return (
    <>
      <div className="border border-gray-200 rounded-sm px-4 py-4">
        <div className="mt-2">
          <h3 className="text-base font-semibold leading-6 text-gray-900">
            Payments
          </h3>
          <div className="border-t border-gray-100 px-2 py-2 sm:px-0">
            {isLoading ? (
              <div className="flex justify-center items-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : normalizedPayments && normalizedPayments.length > 0 ? (
              <Table
                key={key}
                columns={[
                  {
                    header: "Payment Date",
                    accessor: (row) => {
                      const date = new Date(row.payment.paymentDate);
                      return date.toLocaleDateString("en-US", {
                        weekday: 'short',
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        timeZone: "UTC"
                      });
                    },
                  },
                  {
                    header: "Type",
                    accessor: (row) => getPaymentDetails(row),
                  },
                  {
                    header: "Amount",
                    accessor: (row) => {
                      // For split deposit usage entries, show the specific amount used
                      if (row.payment.isDepositUsageEntry) {
                        return (
                          <div className="flex flex-col">
                            <span>${row.payment.amount?.toFixed(2) || "0.00"}</span>
                          </div>
                        );
                      }
                      
                      // For regular payments, show the payment amount
                      return (
                        <div className="flex flex-col">
                          <span>${row.payment.amount?.toFixed(2) || "0.00"}</span>
                        </div>
                      );
                    },
                  },
                  {
                    header: "Invoice",
                    accessor: (row) => {
                      // For split deposit usage entries, show the specific invoice number
                      if (row.payment.isDepositUsageEntry) {
                        return row.payment.invoiceId?.invoiceId || "N/A";
                      }
                      
                      // For regular payments, show the invoice ID
                      return row.payment.invoiceId?.invoiceId || "N/A";
                    },
                  },
                  {
                    header: "QuickBooks",
                    accessor: (row) => (
                      <div className="flex items-center">
                        {row.payment.QBOPaymentId && (
                          <img
                            src={QuickBooksLogo}
                            alt="QuickBooks"
                            className="ml-2 h-5 w-5"
                            title="Loaded to QuickBooks"
                          />
                        )}
                      </div>
                    ),
                  },
                  {
                    header: "Actions",
                    accessor: (row) => {
                      const invoiceId = row.payment?.invoiceId?._id;
                      const invoice = invoices.find((inv) => inv._id === invoiceId);
                      const isRefundedInvoice = invoice?.status === 'refunded';
                      const isDepositUsage = row.payment.isDepositUsageEntry;
                      
                      // Show refund button only if:
                      // 1. User has refund permissions
                      // 2. Invoice is refunded
                      // 3. Payment is not a deposit usage entry (can't refund deposit applications)
                      // 4. Payment is not already refunded
                      if (hasRefundPermission && isRefundedInvoice && !isDepositUsage && !row.payment.isRefund) {
                        return (
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click
                              handleRefundPayment(row);
                            }}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-orange-700 bg-orange-100 border border-orange-200 rounded hover:bg-orange-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
                          >
                            <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z" />
                            </svg>
                            Refund Payment
                          </button>
                        );
                      }
                      
                      return null;
                    },
                  },
                ]}
                data={normalizedPayments}
                onRowClick={handleRowClick}
              />
            ) : (
              <p className="text-sm text-gray-500 py-4">No payments found</p>
            )}
          </div>
        </div>
      </div>
      {showInvoiceModal && (
        <InvoiceModal
          onClose={() => setShowInvoiceModal(false)}
        />
      )}
      
      {/* Payment Refund Modal */}
      {showRefundModal && selectedPaymentForRefund && selectedInvoiceForRefund && (
        <PaymentRefundModal
          isOpen={showRefundModal}
          onClose={() => {
            setShowRefundModal(false);
            setSelectedPaymentForRefund(null);
            setSelectedInvoiceForRefund(null);
          }}
          onConfirm={handlePaymentRefundConfirm}
          payment={selectedPaymentForRefund}
          invoice={selectedInvoiceForRefund}
        />
      )}
    </>
  );
};
